import { useState, useEffect, useCallback, useRef } from 'react';

const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const WARNING_TIME = 14 * 60 * 1000; // 14 minutes - show warning 1 min before expiry

const useSessionTimeout = (onWarning, onExpired) => {
  const [isActive, setIsActive] = useState(true);
  const lastActivityRef = useRef(Date.now());
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const onWarningRef = useRef(onWarning);
  const onExpiredRef = useRef(onExpired);

  // Keep refs in sync
  useEffect(() => { onWarningRef.current = onWarning; }, [onWarning]);
  useEffect(() => { onExpiredRef.current = onExpired; }, [onExpired]);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (warningTimerRef.current) { clearTimeout(warningTimerRef.current); warningTimerRef.current = null; }
  }, []);

  const resetTimers = useCallback(() => {
    clearTimers();
    lastActivityRef.current = Date.now();

    warningTimerRef.current = setTimeout(() => {
      if (onWarningRef.current) onWarningRef.current();
    }, WARNING_TIME);

    idleTimerRef.current = setTimeout(() => {
      if (onExpiredRef.current) onExpiredRef.current();
    }, IDLE_TIMEOUT);
  }, [clearTimers]);

  const pauseTimeout = useCallback(() => {
    setIsActive(false);
    clearTimers();
  }, [clearTimers]);

  const resumeTimeout = useCallback(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (!isActive) return;

    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      resetTimers();
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= IDLE_TIMEOUT) {
          clearTimers();
          if (onExpiredRef.current) onExpiredRef.current();
        } else if (elapsed >= WARNING_TIME) {
          clearTimers();
          if (onWarningRef.current) onWarningRef.current();
        } else {
          resetTimers();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const intervalCheck = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= IDLE_TIMEOUT) {
        clearTimers();
        if (onExpiredRef.current) onExpiredRef.current();
      } else if (elapsed >= WARNING_TIME) {
        clearTimers();
        if (onWarningRef.current) onWarningRef.current();
      }
    }, 30000);

    resetTimers();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalCheck);
      events.forEach(event => window.removeEventListener(event, handleActivity));
      clearTimers();
    };
  }, [isActive, resetTimers, clearTimers]);

  return { resetTimers, pauseTimeout, resumeTimeout, clearTimers };
};

export default useSessionTimeout;
